import { useState, useEffect } from "react";
import axios from "axios";
import { FaCheck, FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useAuth } from "../../../utils/AuthContext";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import TableSkeleton from "../../TableSkeleton";

const UserManagement = () => {
  // ✨ CHANGE HERE: logout ফাংশনটি useAuth থেকে নেওয়া হয়েছে
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "user",
    profilePhoto: "",
  });
  const [newUserFormData, setNewUserFormData] = useState({
    name: "",
    mobile: "",
    role: "user",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState({ value: "", label: "All Roles" });

  const itemsPerPage = 10;
  const token = localStorage.getItem("authToken");
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    if (user && (user.role === "superadmin" || user.role === "admin")) {
      fetchUsers();
    } else {
      setError("Only superadmin or admin can access user management");
    }
  }, [user]);

  useEffect(() => {
    setSelectedRows(new Array(users.length).fill(false));
    setIsHeaderChecked(false);
  }, [users.length, currentPage]);

  const fetchUsers = async () => {
    if (!token) {
      setError("No authentication token found");
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(
        "https://api.muktihospital.com/api/auth/users",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
          },
        }
      );
      // ✨ CHANGE HERE: নতুন ব্যবহারকারীকে প্রথমে দেখানোর জন্য Array রিভার্স করা হয়েছে
      setUsers(response.data.reverse());
      setError("");
    } catch (err) {
      console.error("Error fetching users:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 5MB limit.");
      return;
    }
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email ? user.email.toLowerCase() : "",
      mobile: user.mobile,
      role: user.role,
      profilePhoto: user.profilePhoto || "",
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("No authentication token found");
      return;
    }
    setIsSubmitting(true);

    if (!formData.name || !formData.mobile) {
      toast.error("Name and mobile number are required");
      setIsSubmitting(false);
      return;
    }

    if (user.role !== "superadmin") {
      toast.error("Only superadmin can update users");
      setIsSubmitting(false);
      return;
    }

    try {
      let response;
      if (imageFile) {
        const data = new FormData();
        data.append("name", formData.name);
        data.append("email", formData.email.toLowerCase());
        data.append("mobile", formData.mobile);
        data.append("role", formData.role);
        data.append("image", imageFile);

        response = await axios.put(
          `https://api.muktihospital.com/api/auth/users/${editingUser.id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        const data = {
          name: formData.name,
          email: formData.email ? formData.email.toLowerCase() : null,
          mobile: formData.mobile,
          role: formData.role,
          profilePhoto: formData.profilePhoto || null,
        };

        response = await axios.put(
          `https://api.muktihospital.com/api/auth/users/${editingUser.id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      toast.success("User updated successfully");
      
      if (editingUser.id === user.id && editingUser.role !== formData.role) {
        toast.info("Your role has been changed. You will be logged out.");
        setTimeout(() => {
          logout();
        }, 3000);
      } else {
        setEditingUser(null);
        setImageFile(null);
        setImagePreview(null);
        setSearchTerm("");
        setCurrentPage(1);
        await fetchUsers();
      }

    } catch (err) {
      console.error("Update error:", err.response?.data || err.message);
      toast.error(
        `Failed to update user: ${err.response?.data?.error || err.message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("No authentication token found");
      return;
    }
    setIsSubmitting(true);

    if (!newUserFormData.name || !newUserFormData.mobile) {
      toast.error("Name and mobile number are required");
      setIsSubmitting(false);
      return;
    }

    if (user.role !== "superadmin") {
      toast.error("Only superadmin can add users");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        "https://api.muktihospital.com/api/auth/users",
        {
          name: newUserFormData.name,
          mobile: newUserFormData.mobile,
          role: newUserFormData.role,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("User added successfully");
      setIsAddModalOpen(false);
      setNewUserFormData({ name: "", mobile: "", role: "user" });
      setSearchTerm("");
      setCurrentPage(1);
      await fetchUsers();
    } catch (err) {
      console.error("Add error:", err.response?.data || err.message);
      toast.error(
        `Failed to add user: ${err.response?.data?.error || err.message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (ids) => {
    if (!token) {
      toast.error("No authentication token found");
      return;
    }
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    if (user.role !== "superadmin") {
      toast.error("Only superadmin can delete users");
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        idArray.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/auth/users/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      const updatedUsers = users.filter((user) => !idArray.includes(user.id));
      setUsers(updatedUsers);
      setSelectedRows(new Array(updatedUsers.length).fill(false));
      setIsHeaderChecked(false);
      toast.success(
        `User${idArray.length > 1 ? "s" : ""} deleted successfully`
      );
    } catch (err) {
      console.error("Delete error:", err.response?.data || err.message);
      toast.error(
        `Failed to delete user(s): ${err.response?.data?.error || err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    const currentPageStart = (currentPage - 1) * itemsPerPage;
    const currentPageEnd = Math.min(
      currentPageStart + currentData.length,
      users.length
    );

    const allChecked = currentData.every(
      (_, index) => selectedRows[currentPageStart + index]
    );
    for (let i = currentPageStart; i < currentPageEnd; i++) {
      newSelectedRows[i] = !allChecked;
    }
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!allChecked);
  };

  const handleRowCheckboxChange = (index) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(
      newSelectedRows
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        .every(Boolean)
    );
  };

  const openDeleteModal = (userIds) => {
    setUserToDelete(userIds);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const filteredUsers = users.filter(
    (user) =>
      (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobile?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (selectedRole?.value === "" || user.role === selectedRole?.value)
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const currentData = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "user", label: "User" },
    { value: "patient", label: "Patient" },
    { value: "superadmin", label: "SuperAdmin" },
    { value: "admin", label: "Admin" },
    { value: "pharmacist", label: "Pharmacist" },
    { value: "pathologist", label: "Pathologist" },
    { value: "nicu_in_charge", label: "Nicu In Charge" },
    { value: "hospital_manager", label: "Hospital Manager" },
    { value: "doctor", label: "Doctor" },
  ];

  const skeletonColumns = [
    { width: 16, height: 16, className: "p-3", skeletonClassName: "ml-3" },
    { width: 40, height: 14, className: "p-3 pl-5" },
    { width: 100, height: 14, className: "p-3" },
    { width: 200, height: 14, className: "p-3" },
    { width: 120, height: 14, className: "p-3" },
    { width: 150, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    {
      width: 80,
      height: 28,
      className: "p-3 text-center",
      skeletonClassName: "inline-block",
    },
  ];

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Doctors" },
    { label: "Admin Dashboard — User Management" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading
        title="Admin Dashboard — User Management"
        breadcrumbs={breadcrumbs}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteUser}
        ids={userToDelete}
        entityName="user"
        title="Confirm Delete"
        message={
          Array.isArray(userToDelete) && userToDelete.length > 1
            ? `Are you sure you want to delete these ${userToDelete.length} users?`
            : "Are you sure you want to delete this user?"
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 justify-between border-b border-dashed border—M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              User List
            </h2>
            {user?.role === "superadmin" &&
              selectedRows.some((isSelected) => isSelected) && (
                <button
                  onClick={() => {
                    const selectedUserIds = users
                      .filter((_, index) => selectedRows[index])
                      .map((user) => user.id);
                    openDeleteModal(selectedUserIds);
                  }}
                  className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                  title="Delete selected rows"
                >
                  <RiDeleteBin6Line />
                </button>
              )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <div className="w-48">
              <DynamicSelect
                options={roleOptions}
                value={selectedRole}
                onChange={(selected) => {
                  setSelectedRole(selected);
                  setCurrentPage(1);
                }}
                isSearchable={false}
                placeholder="Filter by Role"
                controlBackgroundColor="#ffffff"
                controlBorderColor="#d1d5db"
              />
            </div>
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text—M-heading-color shrink-0"
              placeholder="Search by name, email, or mobile"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {user?.role === "superadmin" && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-[#73CB64] hover:bg—M-primary-color default-button shrink-0 px-4 py-2 rounded-md text-white"
              >
                + Add User
              </button>
            )}
          </div>
        </div>
        {error && <div className="text-red-600 p-4">{error}</div>}
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                      disabled={user?.role !== "superadmin"}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  SL NO
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  Mobile
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  Email
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  Role
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text—M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton columns={skeletonColumns} rowCount={10} />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-4 text-center">
                    No users found.
                  </td>
                </tr>
              ) : (
                currentData.map((user, index) => {
                  const serialNumber =
                    (currentPage - 1) * itemsPerPage + index + 1;
                  return (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="text-left capitalize font-inter font-medium text-sm text—M-heading-color">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={
                              "user" +
                              ((currentPage - 1) * itemsPerPage + index)
                            }
                            className="hidden peer"
                            checked={
                              selectedRows[
                                (currentPage - 1) * itemsPerPage + index
                              ] || false
                            }
                            onChange={() =>
                              handleRowCheckboxChange(
                                (currentPage - 1) * itemsPerPage + index
                              )
                            }
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={
                              "user" +
                              ((currentPage - 1) * itemsPerPage + index)
                            }
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text—M-heading-color py-5 px-3 pl-5">
                        {serialNumber}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text—M-heading-color py-5 px-3">
                        {user.id}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text—M-heading-color py-5 px-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={
                              user.profilePhoto
                                ? `https://api.muktihospital.com${user.profilePhoto}`
                                : "https://placehold.co/100"
                            }
                            alt={user.name || "User"}
                            className="size-8 rounded-full border border—M-text-color/20 shrink-0 object-cover"
                            onError={(e) =>
                              (e.target.src = "https://placehold.co/100")
                            }
                          />
                          {user.name || "N/A"}
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text—M-heading-color py-5 px-3">
                        {user.mobile || "N/A"}
                      </td>
                      <td className="text-left font-inter font-normal text-sm text—M-heading-color py-5 px-3">
                        {user.email ? user.email.toLowerCase() : "-"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text—M-heading-color py-5 px-3">
                        {user.role || "N/A"}
                      </td>
                      <td className="p-3 text-center space-x-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={() => openDeleteModal([user.id])}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && totalPages > 1 && (
          <div className="p-5 text-end print-hidden">
            <div className="inline-flex items-center justify-between w-full">
              <p className="text-sm text-gray-600">
                Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredUsers.length
                )}{" "}
                of {filteredUsers.length}
              </p>
              <ul className="inline-flex items-center justify-end border-collapse border border—M-text-color/20 rounded-md overflow-hidden">
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>
                {totalPages <= 10
                  ? [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1
                              ? "bg-M-primary-color text-white"
                              : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;
                      for (
                        let i = 1;
                        i <= Math.min(firstPages, totalPages);
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg—M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }
                      for (
                        let i = Math.max(
                          firstPages + 1,
                          totalPages - lastPages + 1
                        );
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg—M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      return pages;
                    })()}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
      {editingUser && user?.role === "superadmin" && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setEditingUser(null)}
          ></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-5">
            <div
              className="bg-white w-full max-w-lg mx-auto rounded-lg shadow-lg relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-dashed border—M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Edit User
                </h2>
              </div>
              <div className="p-5">
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div>
                    <label className="label">Name</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Name"
                        className="input-field"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="label">Email</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <input
                        type="email"
                        name="email"
                        value={formData.email.toLowerCase()}
                        onChange={handleInputChange}
                        placeholder="Email"
                        className="input-field"
                      />
                    )}
                  </div>
                  <div>
                    <label className="label">Mobile Number</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <input
                        type="text"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        placeholder="Mobile"
                        className="input-field"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="label">Role</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <DynamicSelect
                        options={roleOptions}
                        value={roleOptions.find(
                          (opt) => opt.value === formData.role
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            role: selected.value,
                          }))
                        }
                        isSearchable={false}
                        placeholder="Select Role"
                      />
                    )}
                  </div>
                  <div>
                    {isSubmitting ? (
                      <Skeleton
                        height={100}
                        width={100}
                        circle
                        className="mb-4"
                      />
                    ) : imagePreview ? (
                      <div className="mb-4">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-24 h-24 rounded-full object-cover border shadow"
                        />
                      </div>
                    ) : formData.profilePhoto ? (
                      <div className="mb-4">
                        <img
                          src={`https://api.muktihospital.com${formData.profilePhoto}`}
                          alt="Current User"
                          className="w-24 h-24 rounded-full object-cover border shadow"
                          onError={(e) =>
                            (e.target.src = "https://placehold.co/100")
                          }
                        />
                      </div>
                    ) : (
                      <div className="mb-4">
                        <img
                          src="https://placehold.co/100"
                          alt="Placeholder"
                          className="w-24 h-24 rounded-full object-cover border shadow"
                        />
                      </div>
                    )}
                    <div className="mt-4">
                      {isSubmitting ? (
                        <Skeleton height={40} />
                      ) : (
                        <>
                          <p className="text-sm text-gray-600 mt-1">
                            Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB.
                            Allowed types: JPEG, PNG, GIF, WEBP
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            className="border p-2 rounded w-full"
                            disabled={isSubmitting}
                            onChange={handleFileChange}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-4 pt-2">
                    {isSubmitting ? (
                      <>
                        <Skeleton height={40} width={120} />
                        <Skeleton height={40} width={120} />
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingUser(null)}
                          className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="bg-green-600 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
      {isAddModalOpen && user?.role === "superadmin" && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsAddModalOpen(false)}
          ></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-5">
            <div
              className="bg-white w-full max-w-lg mx-auto rounded-lg shadow-lg relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-dashed border—M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Add New User
                </h2>
              </div>
              <div className="p-5">
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div>
                    <label className="label">Name</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <input
                        type="text"
                        name="name"
                        value={newUserFormData.name}
                        onChange={handleNewUserInputChange}
                        placeholder="Name"
                        className="input-field"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="label">Mobile Number</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <input
                        type="text"
                        name="mobile"
                        value={newUserFormData.mobile}
                        onChange={handleNewUserInputChange}
                        placeholder="Mobile"
                        className="input-field"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="label">Role</label>
                    {isSubmitting ? (
                      <Skeleton height={40} />
                    ) : (
                      <DynamicSelect
                        options={roleOptions}
                        value={roleOptions.find(
                          (opt) => opt.value === newUserFormData.role
                        )}
                        onChange={(selected) =>
                          setNewUserFormData((prev) => ({
                            ...prev,
                            role: selected.value,
                          }))
                        }
                        isSearchable={false}
                        placeholder="Select Role"
                      />
                    )}
                  </div>
                  <div className="flex justify-end gap-4 pt-2">
                    {isSubmitting ? (
                      <>
                        <Skeleton height={40} width={120} />
                        <Skeleton height={40} width={120} />
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsAddModalOpen(false)}
                          className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="bg-green-600 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Adding..." : "Add"}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;